import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    createSubteam,
    getTeamSubteams,
    getSubteam,
    updateSubteam,
    deleteSubteam,
    addMemberToSubteam,
    removeMemberFromSubteam,
    getSubteamMembers,
    addSubteamAdmin,
    removeSubteamAdmin
} from 'team/apis/teamSubteamApi';
import { useAuth } from '../../contexts/AuthContext';
import { useError } from '../../contexts/ErrorContext';

const TeamSubteamContext = createContext();

export const TeamSubteamProvider = ({ children }) => {
    const { getCurrentUserId, isAuthenticated, authState } = useAuth();
    const { addError, handleApiCall } = useError();

    // Subteam state management
    const [subteams, setSubteams] = useState({}); // { teamId: subteamArray }
    const [subteamDetails, setSubteamDetails] = useState({}); // { subteamId: subteamData }
    const [subteamMembers, setSubteamMembers] = useState({}); // { subteamId: { members: [], pagination: {} } }
    const [currentSubteamId, setCurrentSubteamId] = useState(null);
    const [subteamError, setSubteamError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Cache management
    const [subteamCache, setSubteamCache] = useState(new Map());
    const [pendingRequests, setPendingRequests] = useState(new Map());

    // Helper function to check if user is authenticated
    const requireAuth = useCallback(() => {
        if (!isAuthenticated() || !getCurrentUserId()) {
            throw new Error('Authentication required');
        }
        return getCurrentUserId();
    }, [isAuthenticated, getCurrentUserId]);

    // Enhanced error handling for subteam operations
    const handleSubteamError = useCallback((error, operation = 'Subteam operation') => {
        console.error(`[TeamSubteamContext] ${operation} error:`, error);

        // Add error to global error context
        addError(error, {
            source: 'subteam',
            type: 'subteam-operation',
            operation,
            timestamp: new Date().toISOString()
        });

        setSubteamError(error.message || 'An unexpected error occurred');
    }, [addError]);

    // Clear subteam error
    const clearSubteamError = useCallback(() => {
        setSubteamError(null);
    }, []);

    // Cache key generator
    const getCacheKey = useCallback((teamId, subteamId = null, operation = 'default') => {
        return subteamId ? `${teamId}-${subteamId}-${operation}` : `${teamId}-${operation}`;
    }, []);

    // Check if data is cached and not stale
    const isCached = useCallback((key, maxAge = 5 * 60 * 1000) => {
        const cached = subteamCache.get(key);
        if (!cached) return false;
        return Date.now() - cached.timestamp < maxAge;
    }, [subteamCache]);

    // Get cached data
    const getCached = useCallback((key) => {
        const cached = subteamCache.get(key);
        return cached ? cached.data : null;
    }, [subteamCache]);

    // Set cached data
    const setCached = useCallback((key, data) => {
        setSubteamCache(prev => new Map(prev.set(key, {
            data,
            timestamp: Date.now()
        })));
    }, []);

    // Check if request is pending
    const isPending = useCallback((key) => {
        return pendingRequests.has(key);
    }, [pendingRequests]);

    // Set pending request
    const setPending = useCallback((key, promise) => {
        setPendingRequests(prev => new Map(prev.set(key, promise)));
        return promise;
    }, []);

    // Clear pending request
    const clearPending = useCallback((key) => {
        setPendingRequests(prev => {
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
        });
    }, []);

    // Create subteam
    const createSubteamHandler = useCallback(async (teamId, subteamData) => {
        const cacheKey = getCacheKey(teamId, null, 'create');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => createSubteam(teamId, subteamData),
                'Creating subteam'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                const newSubteam = result.data;

                // Update state
                setSubteams(prev => ({
                    ...prev,
                    [teamId]: [...(prev[teamId] || []), newSubteam]
                }));

                setSubteamDetails(prev => ({
                    ...prev,
                    [newSubteam.id]: newSubteam
                }));

                // Invalidate related caches
                const subteamsKey = getCacheKey(teamId, null, 'subteams');
                subteamCache.delete(subteamsKey);

                console.log('[TeamSubteamContext] Subteam created successfully:', newSubteam);
                return { success: true, data: newSubteam };
            } else {
                throw new Error(result.error || 'Failed to create subteam');
            }
        } catch (error) {
            handleSubteamError(error, 'Creating subteam');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Get team subteams
    const getTeamSubteamsHandler = useCallback(async (teamId, options = {}) => {
        const cacheKey = getCacheKey(teamId, null, 'subteams');

        try {
            requireAuth();

            // Check cache first
            if (isCached(cacheKey) && !options.forceRefresh) {
                const cachedData = getCached(cacheKey);
                return { success: true, data: cachedData };
            }

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => getTeamSubteams(teamId, options),
                'Fetching team subteams'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                const subteamsData = result.data;

                // Update state
                setSubteams(prev => ({
                    ...prev,
                    [teamId]: subteamsData
                }));

                // Cache the result
                setCached(cacheKey, subteamsData);

                console.log('[TeamSubteamContext] Team subteams fetched successfully:', subteamsData);
                return { success: true, data: subteamsData };
            } else {
                throw new Error(result.error || 'Failed to fetch team subteams');
            }
        } catch (error) {
            handleSubteamError(error, 'Fetching team subteams');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isCached, getCached, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, setCached]);

    // Get subteam details
    const getSubteamHandler = useCallback(async (subteamId, forceRefresh = false) => {
        const cacheKey = getCacheKey(null, subteamId, 'details');

        try {
            requireAuth();

            // Check cache first
            if (isCached(cacheKey) && !forceRefresh) {
                const cachedData = getCached(cacheKey);
                return { success: true, data: cachedData };
            }

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => getSubteam(subteamId),
                'Fetching subteam details'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                const subteamData = result.data;

                // Update state
                setSubteamDetails(prev => ({
                    ...prev,
                    [subteamId]: subteamData
                }));

                // Cache the result
                setCached(cacheKey, subteamData);

                console.log('[TeamSubteamContext] Subteam details fetched successfully:', subteamData);
                return { success: true, data: subteamData };
            } else {
                throw new Error(result.error || 'Failed to fetch subteam details');
            }
        } catch (error) {
            handleSubteamError(error, 'Fetching subteam details');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isCached, getCached, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, setCached]);

    // Update subteam
    const updateSubteamHandler = useCallback(async (subteamId, updateData) => {
        const cacheKey = getCacheKey(null, subteamId, 'update');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => updateSubteam(subteamId, updateData),
                'Updating subteam'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                const updatedSubteam = result.data;

                // Update state
                setSubteamDetails(prev => ({
                    ...prev,
                    [subteamId]: updatedSubteam
                }));

                // Update in subteams array if it exists
                setSubteams(prev => {
                    const newSubteams = { ...prev };
                    Object.keys(newSubteams).forEach(teamId => {
                        newSubteams[teamId] = newSubteams[teamId].map(subteam =>
                            subteam.id === subteamId ? updatedSubteam : subteam
                        );
                    });
                    return newSubteams;
                });

                // Invalidate related caches
                const detailsKey = getCacheKey(null, subteamId, 'details');
                subteamCache.delete(detailsKey);

                console.log('[TeamSubteamContext] Subteam updated successfully:', updatedSubteam);
                return { success: true, data: updatedSubteam };
            } else {
                throw new Error(result.error || 'Failed to update subteam');
            }
        } catch (error) {
            handleSubteamError(error, 'Updating subteam');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Delete subteam
    const deleteSubteamHandler = useCallback(async (subteamId) => {
        const cacheKey = getCacheKey(null, subteamId, 'delete');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => deleteSubteam(subteamId),
                'Deleting subteam'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                // Update state
                setSubteamDetails(prev => {
                    const newDetails = { ...prev };
                    delete newDetails[subteamId];
                    return newDetails;
                });

                setSubteams(prev => {
                    const newSubteams = { ...prev };
                    Object.keys(newSubteams).forEach(teamId => {
                        newSubteams[teamId] = newSubteams[teamId].filter(subteam => subteam.id !== subteamId);
                    });
                    return newSubteams;
                });

                // Clear subteam members
                setSubteamMembers(prev => {
                    const newMembers = { ...prev };
                    delete newMembers[subteamId];
                    return newMembers;
                });

                // Invalidate related caches
                const detailsKey = getCacheKey(null, subteamId, 'details');
                subteamCache.delete(detailsKey);

                console.log('[TeamSubteamContext] Subteam deleted successfully');
                return { success: true };
            } else {
                throw new Error(result.error || 'Failed to delete subteam');
            }
        } catch (error) {
            handleSubteamError(error, 'Deleting subteam');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Add member to subteam
    const addMemberToSubteamHandler = useCallback(async (subteamId, userId) => {
        const cacheKey = getCacheKey(null, subteamId, 'add-member');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => addMemberToSubteam(subteamId, userId),
                'Adding member to subteam'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                // Invalidate related caches
                const membersKey = getCacheKey(null, subteamId, 'members');
                subteamCache.delete(membersKey);

                console.log('[TeamSubteamContext] Member added to subteam successfully');
                return { success: true, data: result.data };
            } else {
                throw new Error(result.error || 'Failed to add member to subteam');
            }
        } catch (error) {
            handleSubteamError(error, 'Adding member to subteam');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Remove member from subteam
    const removeMemberFromSubteamHandler = useCallback(async (subteamId, userId) => {
        const cacheKey = getCacheKey(null, subteamId, 'remove-member');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => removeMemberFromSubteam(subteamId, userId),
                'Removing member from subteam'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                // Update state
                setSubteamMembers(prev => {
                    const subteamData = prev[subteamId];
                    if (subteamData) {
                        return {
                            ...prev,
                            [subteamId]: {
                                ...subteamData,
                                members: subteamData.members.filter(member => member.userId !== userId)
                            }
                        };
                    }
                    return prev;
                });

                // Invalidate related caches
                const membersKey = getCacheKey(null, subteamId, 'members');
                subteamCache.delete(membersKey);

                console.log('[TeamSubteamContext] Member removed from subteam successfully');
                return { success: true };
            } else {
                throw new Error(result.error || 'Failed to remove member from subteam');
            }
        } catch (error) {
            handleSubteamError(error, 'Removing member from subteam');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Get subteam members
    const getSubteamMembersHandler = useCallback(async (subteamId, options = {}) => {
        const cacheKey = getCacheKey(null, subteamId, 'members');

        try {
            requireAuth();

            // Check cache first
            if (isCached(cacheKey) && !options.forceRefresh) {
                const cachedData = getCached(cacheKey);
                return { success: true, data: cachedData };
            }

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => getSubteamMembers(subteamId, options),
                'Fetching subteam members'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                const membersData = result.data;

                // Update state
                setSubteamMembers(prev => ({
                    ...prev,
                    [subteamId]: membersData
                }));

                // Cache the result
                setCached(cacheKey, membersData);

                console.log('[TeamSubteamContext] Subteam members fetched successfully:', membersData);
                return { success: true, data: membersData };
            } else {
                throw new Error(result.error || 'Failed to fetch subteam members');
            }
        } catch (error) {
            handleSubteamError(error, 'Fetching subteam members');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isCached, getCached, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, setCached]);

    // Add subteam admin
    const addSubteamAdminHandler = useCallback(async (subteamId, userId) => {
        const cacheKey = getCacheKey(null, subteamId, 'add-admin');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => addSubteamAdmin(subteamId, userId),
                'Adding subteam admin'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                // Invalidate related caches
                const detailsKey = getCacheKey(null, subteamId, 'details');
                const membersKey = getCacheKey(null, subteamId, 'members');
                subteamCache.delete(detailsKey);
                subteamCache.delete(membersKey);

                console.log('[TeamSubteamContext] Subteam admin added successfully');
                return { success: true, data: result.data };
            } else {
                throw new Error(result.error || 'Failed to add subteam admin');
            }
        } catch (error) {
            handleSubteamError(error, 'Adding subteam admin');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Remove subteam admin
    const removeSubteamAdminHandler = useCallback(async (subteamId, userId) => {
        const cacheKey = getCacheKey(null, subteamId, 'remove-admin');

        try {
            requireAuth();

            if (isPending(cacheKey)) {
                return await pendingRequests.get(cacheKey);
            }

            setLoading(true);
            clearSubteamError();

            const promise = handleApiCall(
                () => removeSubteamAdmin(subteamId, userId),
                'Removing subteam admin'
            );

            setPending(cacheKey, promise);
            const result = await promise;

            if (result.success) {
                // Invalidate related caches
                const detailsKey = getCacheKey(null, subteamId, 'details');
                const membersKey = getCacheKey(null, subteamId, 'members');
                subteamCache.delete(detailsKey);
                subteamCache.delete(membersKey);

                console.log('[TeamSubteamContext] Subteam admin removed successfully');
                return { success: true, data: result.data };
            } else {
                throw new Error(result.error || 'Failed to remove subteam admin');
            }
        } catch (error) {
            handleSubteamError(error, 'Removing subteam admin');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
            clearPending(cacheKey);
        }
    }, [requireAuth, getCacheKey, isPending, pendingRequests, handleApiCall, clearSubteamError, handleSubteamError, subteamCache]);

    // Set current subteam
    const setCurrentSubteam = useCallback((subteamId) => {
        setCurrentSubteamId(subteamId);
        console.log('[TeamSubteamContext] Current subteam set to:', subteamId);
    }, []);

    // Get current subteam
    const getCurrentSubteam = useCallback(() => {
        return currentSubteamId ? subteamDetails[currentSubteamId] : null;
    }, [currentSubteamId, subteamDetails]);

    // Get subteam by ID from current state
    const getSubteamById = useCallback((subteamId) => {
        return subteamDetails[subteamId] || null;
    }, [subteamDetails]);

    // Get subteams by team ID from current state
    const getSubteamsByTeamId = useCallback((teamId) => {
        return subteams[teamId] || [];
    }, [subteams]);

    // Clear all subteam data
    const clearAllSubteamData = useCallback(() => {
        setSubteams({});
        setSubteamDetails({});
        setSubteamMembers({});
        setSubteamCache(new Map());
        setPendingRequests(new Map());
        setCurrentSubteamId(null);
        clearSubteamError();
        console.log('[TeamSubteamContext] All subteam data cleared');
    }, [clearSubteamError]);

    // Context value
    const value = {
        // State
        subteams,
        subteamDetails,
        subteamMembers,
        currentSubteamId,
        subteamError,
        loading,

        // Actions
        createSubteam: createSubteamHandler,
        getTeamSubteams: getTeamSubteamsHandler,
        getSubteam: getSubteamHandler,
        updateSubteam: updateSubteamHandler,
        deleteSubteam: deleteSubteamHandler,
        addMemberToSubteam: addMemberToSubteamHandler,
        removeMemberFromSubteam: removeMemberFromSubteamHandler,
        getSubteamMembers: getSubteamMembersHandler,
        addSubteamAdmin: addSubteamAdminHandler,
        removeSubteamAdmin: removeSubteamAdminHandler,

        // Utility functions
        setCurrentSubteam,
        getCurrentSubteam,
        getSubteamById,
        getSubteamsByTeamId,
        clearAllSubteamData,
        clearSubteamError
    };

    return (
        <TeamSubteamContext.Provider value={value}>
            {children}
        </TeamSubteamContext.Provider>
    );
};

export const useTeamSubteam = () => {
    const context = useContext(TeamSubteamContext);
    if (!context) {
        throw new Error('useTeamSubteam must be used within a TeamSubteamProvider');
    }
    return context;
};
